//
// Software Name: Orange Design System Charts
// SPDX-FileCopyrightText: Copyright (c) 2023 - 2026 Orange SA
// SPDX-License-Identifier: MIT
//
// This software is distributed under the MIT license.
//
import { ODSChartsLineStyle, ODSChartsVisualMapColorRangeMode } from '../ods-chart-theme';
/**
 * Charts type
 */
export var ODSChartsTypes;
(function (ODSChartsTypes) {
    ODSChartsTypes["DEFAULT"] = "DEFAULT";
    ODSChartsTypes["LINE"] = "LINE";
    ODSChartsTypes["BAR"] = "BAR";
    ODSChartsTypes["LINE_AND_BAR"] = "LINE_AND_BAR";
    ODSChartsTypes["PIE"] = "PIE";
    ODSChartsTypes["DONUT"] = "DONUT";
    ODSChartsTypes["HORIZONTAL_GAUGE"] = "HORIZONTAL_GAUGE";
    ODSChartsTypes["SEMI_CIRCULAR_GAUGE"] = "SEMI_CIRCULAR_GAUGE";
    ODSChartsTypes["CIRCULAR_GAUGE"] = "CIRCULAR_GAUGE";
    ODSChartsTypes["DIAL_GAUGE"] = "DIAL_GAUGE";
    ODSChartsTypes["CHOROPLETH_MAP"] = "CHOROPLETH_MAP";
    ODSChartsTypes["BUBBLE_MAP"] = "BUBBLE_MAP";
    ODSChartsTypes["HEATMAP"] = "HEATMAP";
})(ODSChartsTypes || (ODSChartsTypes = {}));
/**
 * Configuration of a line chart.
 */
export class ODSChartsLineConfiguration {
}
/**
 * Configuration of a gauge chart.
 */
export class ODSChartsGaugeConfiguration {
}
/**
 * Configuration of dial gauge chart.
 * It extends the ODSChartsGaugeConfiguration to include specific properties for dial points.
 */
export class ODSChartsDialGaugeConfiguration extends ODSChartsGaugeConfiguration {
}
/**
 * Configuration of a chart using a visualMap.
 */
export class ODSChartsVisualMapConfiguration {
}
/**
 * ChartsConfiguration is used to specify the configuration specific to a type of graph.
 *
 * For the moment can be:
 * - {@link getLineChartConfiguration} : to build a configuration of a line chart
 * - {@link getBarChartConfiguration} : to build a configuration of a bar chart
 * - {@link getLineAndBarChartConfiguration} : to build a configuration of a bar and line chart
 * - {@link getPieChartConfiguration} : to build a configuration of a pie chart
 * - {@link getDonutChartConfiguration} : to build a configuration of a donut chart
 * - {@link getHorizontalGaugeChartConfiguration} : to build a configuration of a horizontal gauge
 * - {@link getSemiCircularGaugeChartConfiguration} : to build a configuration of a semicircular gauge
 * - {@link getCircularGaugeChartConfiguration} : to build a configuration of a circular gauge
 * - {@link getDialGaugeChartConfiguration} : to build a configuration of a dial gauge
 * - {@link getChoroplethMapChartConfiguration} : to build a configuration of a choropleth map
 * - {@link getBubbleMapChartConfiguration} : to build a configuration of a bubble map
 * - {@link getHeatmapChartConfiguration} : to build a configuration of a heatmap
 */
export class ODSChartsConfiguration {
    constructor(type = ODSChartsTypes.DEFAULT) {
        this.type = type;
    }
    getLegendConfiguration(_themeOptions, _dataOptions) {
        return Object.assign(Object.assign({ orient: 'horizontal', show: true }, _themeOptions === null || _themeOptions === void 0 ? void 0 : _themeOptions.legend), _dataOptions === null || _dataOptions === void 0 ? void 0 : _dataOptions.legend);
    }
    /**
     * For internal use only.
     * This method should not be called directly by the user. It is used internally by the class to build the configuration of the charts.
     * @returns The default configuration of a chart.
     * @internal
     */
    getDefaultConfiguration() {
        return {};
    }
    /**
     * For internal use only.
     * This method should not be called directly by the user. It is used internally by the class to build the configuration of the charts.
     * @param _serie The serie to get the configuration for.
     * @returns The configuration of the serie.
     * @internal
     */
    getSerieConfiguration(_serie, _themeOptions, _dataOptions) {
        return {};
    }
    /**
     * Get the empty default configuration of a chart.
     * This method should not be used. Prefer to use the specific methods to get the configuration of a chart.
     * For example, use {@link getLineChartConfiguration} to get the configuration of a line chart.
     * @returns The default configuration of a chart.
     */
    static getDefaultChartConfiguration() {
        return new ODSChartsConfiguration();
    }
    /**
     * Get the configuration of a line chart.
     * @param config Configuration of the line chart, can be used to specify the line style. It can be:
     * - {@link ODSChartsLineStyle.SMOOTH}: smooth line
     * - {@link ODSChartsLineStyle.BROKEN}: broken line
     * - {@link ODSChartsLineStyle.BROKEN_WITH_POINTS}: broken line with points
     * - {@link ODSChartsLineStyle.SMOOTH_WITH_POINTS}: smooth line with points
     * - {@link ODSChartsLineStyle.POINTS}: only points displayed
     * @returns Configuration of a line chart.
     */
    static getLineChartConfiguration(config = { lineStyle: ODSChartsLineStyle.SMOOTH }) {
        return new ODSChartsLine(config === null || config === void 0 ? void 0 : config.lineStyle);
    }
    /**
     * Get the configuration of a bar chart.
     * @returns Configuration of a bar chart.
     */
    static getBarChartConfiguration() {
        return new ODSChartsBar();
    }
    /**
     * get the configuration of a line and bar chart.
     * @param config Configuration of the line and bar chart, can be used to specify the line style.
     *  It can be:
     * - {@link ODSChartsLineStyle.SMOOTH}: smooth line
     * - {@link ODSChartsLineStyle.BROKEN}: broken line
     * - {@link ODSChartsLineStyle.BROKEN_WITH_POINTS}: broken line with points
     * - {@link ODSChartsLineStyle.SMOOTH_WITH_POINTS}: smooth line with points
     * - {@link ODSChartsLineStyle.POINTS}: only points displayed
     * @returns Configuration of a line and bar chart.
     */
    static getLineAndBarChartConfiguration(config = { lineStyle: ODSChartsLineStyle.SMOOTH }) {
        return new ODSChartsLineAndBar(config === null || config === void 0 ? void 0 : config.lineStyle);
    }
    /**
     * Get the configuration of a pie chart.
     * @returns Configuration of a pie chart.
     */
    static getPieChartConfiguration() {
        return new ODSChartsPie();
    }
    /**
     * Get the configuration of a donut chart.
     * @returns Configuration of a donut chart.
     */
    static getDonutChartConfiguration() {
        return new ODSChartsDonut();
    }
    /**
     * Get the configuration of a horizontal gauge chart.
     * This chart is a bar chart with a horizontal orientation, used to display a single value within a range.
     * It is useful for showing progress or performance against a target.
     * @param config Configuration of the horizontal gauge chart, must be used to specify the minimum and maximum values.
     *               The chart will display a bar that fills the space between these two values.
     * @returns Configuration of the horizontal gauge chart.
     */
    static getHorizontalGaugeChartConfiguration(config) {
        return new ODSChartsHorizontalGauge(config === null || config === void 0 ? void 0 : config.minValue, config === null || config === void 0 ? void 0 : config.maxValue);
    }
    /**
     * Get the configuration of a semi-circular gauge chart.
     * @param config Configuration of the semi-circular gauge chart, must be used to specify the minimum and maximum values.
     * @returns Configuration of the semi-circular gauge chart.
     */
    static getSemiCircularGaugeChartConfiguration(config) {
        return new ODSChartsSemiCircularGauge(config === null || config === void 0 ? void 0 : config.minValue, config === null || config === void 0 ? void 0 : config.maxValue);
    }
    /**
     * Get the configuration of a circular gauge chart.
     * @param config Configuration of the circular gauge chart, must be used to specify the minimum and maximum values.
     * @returns Configuration of the circular gauge chart.
     */
    static getCircularGaugeChartConfiguration(config) {
        return new ODSChartsCircularGauge(config === null || config === void 0 ? void 0 : config.minValue, config === null || config === void 0 ? void 0 : config.maxValue);
    }
    /**
     * Get the configuration of a dial gauge chart.
     * @param config Configuration of the dial gauge chart, must be used to specify the minimum and maximum values and the dial parts.
     * @returns Configuration of the dial gauge chart.
     */
    static getDialGaugeChartConfiguration(config) {
        return new ODSChartsDialGauge(config === null || config === void 0 ? void 0 : config.minValue, config === null || config === void 0 ? void 0 : config.maxValue, config === null || config === void 0 ? void 0 : config.dialPoints);
    }
    /**
     * Get the configuration of a choropleth map chart.
     * A choropleth map is a thematic map where areas are shaded or patterned based on a data variable.
     * @returns Configuration of the choropleth map chart.
     */
    static getChoroplethMapChartConfiguration(config = {}) {
        return new ODSChartsChoroplethMap(config === null || config === void 0 ? void 0 : config.visualMapColorRangeMode);
    }
    /**
     * Get the configuration of a bubble map chart.
     * A bubble map displays data as circles (bubbles) on a geographic map, where bubble size represents a value.
     * @returns Configuration of the bubble map chart.
     */
    static getBubbleMapChartConfiguration(config = {}) {
        return new ODSChartsBubbleMap(config === null || config === void 0 ? void 0 : config.visualMapColorRangeMode);
    }
    /**
     * Get the configuration of a heatmap chart.
     * @returns Configuration of the heatmap chart.
     */
    static getHeatmapChartConfiguration(config = {}) {
        return new ODSChartsHeatmap(config === null || config === void 0 ? void 0 : config.visualMapColorRangeMode);
    }
}
/**
 * Configuration of a line chart of one of the type
 * - {@link ODSChartsTypes.LINE}
 * - {@link ODSChartsTypes.LINE_AND_BAR}
 */
class ODSChartsLineType extends ODSChartsConfiguration {
    constructor(type = ODSChartsTypes.LINE, lineStyle) {
        super(type);
        this.lineStyle = lineStyle;
        if (undefined === this.lineStyle) {
            this.lineStyle = ODSChartsLineStyle.SMOOTH;
        }
    }
}
/**
 * Configuration of a chart of type {@link ODSChartsTypes.LINE_AND_BAR}
 */
class ODSChartsLineAndBar extends ODSChartsLineType {
    constructor(lineStyle) {
        super(ODSChartsTypes.LINE_AND_BAR, lineStyle);
    }
}
/**
 * Configuration of a chart of type {@link ODSChartsTypes.LINE}
 */
class ODSChartsLine extends ODSChartsLineType {
    constructor(lineStyle) {
        super(ODSChartsTypes.LINE, lineStyle);
    }
}
/**
 * Configuration of a bar chart of one of the type:
 * - {@link ODSChartsTypes.BAR}
 * - {@link ODSChartsTypes.STACKED_BAR}
 */
class ODSChartsBarType extends ODSChartsConfiguration {
    constructor(type) {
        super(type);
    }
}
/**
 * Configuration of a chart of type {@link ODSChartsTypes.BAR}
 */
class ODSChartsBar extends ODSChartsBarType {
    constructor() {
        super(ODSChartsTypes.BAR);
    }
}
/**
 * Configuration of a pie chart of type {@link ODSChartsTypes.PIE}
 */
class ODSChartsPie extends ODSChartsConfiguration {
    getSerieConfiguration(serie, _themeOptions, _dataOptions) {
        if (serie.type !== 'pie') {
            return {};
        }
        const legend = this.getLegendConfiguration(_themeOptions, _dataOptions);
        return Object.assign({ label: { show: false, position: 'outside' }, labelLine: { show: false } }, (legend.show && legend.orient === 'horizontal'
            ? {
                center: ['50%', '40%'],
                radius: ['0%', '80%'],
            }
            : legend.show && legend.orient === 'vertical'
                ? {
                    radius: ['0%', '95%'],
                    center: ['40%', '50%'],
                }
                : {
                    radius: ['0%', '95%'],
                }));
    }
    constructor() {
        super(ODSChartsTypes.PIE);
    }
}
/**
 * Configuration of a chart of type {@link ODSChartsTypes.HorizontalGauge}
 */
class ODSChartsDonut extends ODSChartsConfiguration {
    getSerieConfiguration(serie, _themeOptions, _dataOptions) {
        if (serie.type !== 'pie') {
            return {};
        }
        const legend = this.getLegendConfiguration(_themeOptions, _dataOptions);
        return Object.assign({ label: { show: false, position: 'center' }, labelLine: { show: false }, emphasis: {
                label: {
                    show: true,
                    fontSize: 35,
                    fontWeight: 700,
                    formatter: '{d}%',
                },
            } }, (legend.show && legend.orient === 'horizontal'
            ? {
                center: ['50%', '40%'],
                radius: ['65%', '80%'],
            }
            : legend.show && legend.orient === 'vertical'
                ? {
                    radius: ['80%', '95%'],
                    center: ['40%', '50%'],
                }
                : {
                    radius: ['80%', '95%'],
                }));
    }
    constructor() {
        super(ODSChartsTypes.DONUT);
    }
}
/**
 * configuration of a gauche chart of one the type
 * - {@link ODSChartsTypes.DIAL_GAUGE}
 * - {@link ODSChartsTypes.HORIZONTAL_GAUGE}
 * - {@link ODSChartsTypes.CIRCULAR_GAUGE}
 * - {@link ODSChartsTypes.CIRCLE_GAUGE}
 */
class ODSChartsGaugeType extends ODSChartsConfiguration {
    constructor(type) {
        super(type);
    }
}
/**
 * Configuration of a chart of type {@link ODSChartsTypes.HORIZONTAL_GAUGE}
 */
class ODSChartsHorizontalGauge extends ODSChartsGaugeType {
    constructor(minValue, maxValue) {
        super(ODSChartsTypes.HORIZONTAL_GAUGE);
        this.minValue = minValue;
        this.maxValue = maxValue;
    }
    getDefaultConfiguration() {
        return {
            grid: {
                left: 20,
                top: 32,
                right: 20,
                height: 32,
            },
            yAxis: {
                show: true,
                type: 'category',
                axisLabel: {
                    margin: 0,
                    lineHeight: 50,
                    inside: true,
                    verticalAlign: 'bottom',
                },
                axisLine: {
                    show: false,
                },
                splitLine: {
                    show: false,
                },
            },
            xAxis: Object.assign(Object.assign(Object.assign({ type: 'value', position: 'top' }, (undefined !== this.minValue ? { min: this.minValue } : {})), (undefined !== this.maxValue ? { max: this.maxValue } : {})), { splitNumber: 1, axisLine: { show: false }, axisTick: { show: false }, splitLine: {
                    show: false,
                }, axisLabel: {
                    formatter: (val) => ((this.minValue || 0) === val ? '' : val),
                    align: 'right',
                    show: true,
                    lineHeight: 0,
                    fontWeight: 'normal',
                } }),
        };
    }
    getSerieConfiguration(serie, _themeOptions, _dataOptions) {
        if ('bar' !== serie.type) {
            return {};
        }
        return {
            pointer: {
                show: false,
            },
            barWidth: 32,
            showBackground: true,
            backgroundStyle: {
                color: 'var(--bs-gray-500)',
            },
            itemStyle: {
                barBorderWidth: 0,
            },
        };
    }
}
/**
 * Configuration of a circular gauge of one of the type
 * - {@link ODSChartsTypes.SEMI_CIRCULAR_GAUGE}
 * - {@link ODSChartsTypes.CIRCULAR_GAUGE}
 * - {@link ODSChartsTypes.DIAL_GAUGE}
 */
class ODSChartsCircularGaugeType extends ODSChartsConfiguration {
    constructor(type = ODSChartsTypes.SEMI_CIRCULAR_GAUGE, minValue, maxValue) {
        super(type);
        this.minValue = minValue;
        this.maxValue = maxValue;
    }
    getSerieConfiguration(serie, _themeOptions, _dataOptions) {
        if (serie.type !== 'gauge') {
            return {};
        }
        const circular = ODSChartsTypes.CIRCULAR_GAUGE === this.type;
        const minValue = undefined !== serie.min ? serie.min : undefined !== this.minValue ? this.minValue : 0;
        const maxValue = undefined !== serie.max ? serie.max : undefined !== this.maxValue ? this.maxValue : 100;
        return {
            endAngle: circular ? -270 : 0,
            startAngle: circular ? 90 : 180,
            min: minValue,
            max: maxValue,
            radius: circular ? '90%' : '150%',
            center: ['50%', circular ? '50%' : '75%'],
            splitNumber: 1,
            detail: {
                offsetCenter: [0, circular ? 0 : -10],
                color: 'var(--bs-body-color, #000)',
                fontSize: 40,
                fontFamily: 'var(--bs-font-sans-serif, "Helvetica Neue")',
                fontWeight: 'bold',
            },
            progress: {
                show: true,
                width: 32,
            },
            splitLine: {
                show: false,
            },
            axisTick: {
                show: false,
            },
            pointer: {
                show: false,
            },
            axisLabel: {
                show: circular ? false : true,
                distance: -50,
                color: 'var(--bs-body-color, #000)',
                fontSize: 14,
                fontFamily: 'var(--bs-font-sans-serif, "Helvetica Neue")',
                fontWeight: 400,
            },
            axisLine: {
                roundCap: false,
                lineStyle: {
                    width: 32,
                    color: [[1, 'var(--bs-light)']],
                },
            },
        };
    }
}
/**
 * Configuration of a chart of type {@link ODSChartsTypes.CIRCULAR_GAUGE}
 */
class ODSChartsSemiCircularGauge extends ODSChartsCircularGaugeType {
    constructor(minValue, maxValue) {
        super(ODSChartsTypes.SEMI_CIRCULAR_GAUGE, minValue, maxValue);
    }
}
/**
 * Configuration of a chart of type {@link ODSChartsTypes.CIRCULAR_GAUGE}
 */
class ODSChartsDialGauge extends ODSChartsCircularGaugeType {
    constructor(minValue, maxValue, dialPoints) {
        super(ODSChartsTypes.DIAL_GAUGE, minValue, maxValue);
        this.dialPoints = dialPoints;
        if (undefined === minValue && dialPoints && dialPoints.length > 1) {
            this.minValue = dialPoints[0].value;
        }
        if (undefined === maxValue && dialPoints && dialPoints.length > 0) {
            this.maxValue = dialPoints[dialPoints.length - 1].value;
        }
    }
    // Fonction pour calculer le PGCD de deux nombres
    gcd(a, b) {
        a = Math.abs(a);
        b = Math.abs(b);
        while (b !== 0) {
            const temp = b;
            b = a % b;
            a = temp;
        }
        return a;
    }
    // Fonction pour calculer le PGCD d’un tableau de nombres
    gcdArray(arr) {
        return arr.reduce((acc, val) => this.gcd(acc, val), arr[0]);
    }
    getSerieConfiguration(serie, _themeOptions, _dataOptions) {
        if (serie.type !== 'gauge') {
            return {};
        }
        const config = super.getSerieConfiguration(serie, _themeOptions, _dataOptions);
        const minValue = config.min;
        const maxValue = config.max;
        const dialPoints = this.dialPoints && 0 < this.dialPoints.length ? this.dialPoints : [{ value: minValue }, { value: maxValue }];
        if (minValue !== dialPoints[0].value) {
            dialPoints.unshift({ value: minValue });
        }
        if (maxValue !== dialPoints[dialPoints.length - 1].value) {
            dialPoints.push({ value: maxValue });
        }
        const steps = dialPoints.reduce((foundPeriods, currentPart, index) => {
            if (index > 0) {
                foundPeriods.push(currentPart.value - dialPoints[index - 1].value);
            }
            return foundPeriods;
        }, []);
        const commonStep = this.gcdArray(steps);
        const splitNumber = (maxValue - minValue) / commonStep;
        return Object.assign(Object.assign({}, config), { radius: '125%', splitNumber: splitNumber, detail: {
                offsetCenter: [0, 30],
                color: 'var(--bs-body-color, #000)',
                fontSize: 40,
                fontFamily: 'var(--bs-font-sans-serif, "Helvetica Neue")',
                fontWeight: 'bold',
            }, progress: {
                show: false,
            }, pointer: {
                show: true,
                width: 50,
                icon: 'var(--ods-dial-gauge-pointer, image://data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20viewBox%3D%22147%2065.2635%2023%20104.4226%22%20width%3D%2223px%22%20height%3D%22107.735px%22%3E%0A%20%20%3Cpath%20d%3D%22M%20123.034%2079.908%20L%20179.603%20150.618%20L%20193.745%20136.476%20L%20123.034%2079.908%20Z%22%20fill%3D%22%23000%22%20style%3D%22stroke-width%3A%201%3B%20transform-origin%3A%20158.389px%20115.263px%3B%22%20transform%3D%22matrix(0.70710701%2C%200.70710701%2C%20-0.70710701%2C%200.70710701%2C%200.00000871%2C%20-0.0000095)%22%2F%3E%0A%20%20%3Ccircle%20cx%3D%22131.469%22%20cy%3D%2290.553%22%20r%3D%2210%22%20fill%3D%22%23000%22%20style%3D%22stroke-width%3A%201%3B%20transform-origin%3A%20131.469px%2090.553px%3B%22%20transform%3D%22matrix(-0.70710701%2C%20-0.70710701%2C%200.70710701%2C%20-0.70710701%2C%2026.91516753%2C%2064.99089065)%22%2F%3E%0A%3C%2Fsvg%3E)',
                itemStyle: {
                    color: 'var(--bs-body-color, #000)',
                },
                offsetCenter: [0, 0],
            }, axisLabel: {
                show: true,
                distance: -40,
                color: 'var(--bs-body-color, #000)',
                fontSize: 14,
                fontFamily: 'var(--bs-font-sans-serif, "Helvetica Neue")',
                fontWeight: 400,
                formatter: function (value) {
                    const labelValue = dialPoints.find((part) => value === part.value);
                    return labelValue && labelValue.label ? labelValue.label : '';
                },
            }, axisLine: {
                roundCap: false,
                lineStyle: {
                    width: 32,
                    /* build the color sets of the gauge, like :
                        [
                          [0.498, 'var(--ouds-charts-color-functional-positive)'],
                          [0.502, 'transparent'],
                          [0.748, 'var(--ouds-charts-color-functional-warning)'],
                          [0.752, 'transparent'],
                          [1, 'var(--ouds-charts-color-functional-negative)'],
                        ]
                    */
                    color: dialPoints.reduce((colors, part, index) => {
                        if (index > 0 && part.beforeColor) {
                            const valuePct = part.value / (maxValue - minValue);
                            if (dialPoints.length - 1 === index) {
                                colors.push([valuePct, part.beforeColor]);
                            }
                            else {
                                colors.push([valuePct - 0.002, part.beforeColor]);
                                colors.push([valuePct + 0.002, 'transparent']);
                            }
                        }
                        else if (dialPoints.length - 1 === index) {
                            colors.push([1, 'var(--bs-light)']);
                        }
                        return colors;
                    }, []),
                },
            } });
    }
}
/**
 * Configuration of a chart of type {@link ODSChartsTypes.CIRCULAR_GAUGE}
 */
class ODSChartsCircularGauge extends ODSChartsCircularGaugeType {
    constructor(minValue, maxValue) {
        super(ODSChartsTypes.CIRCULAR_GAUGE, minValue, maxValue);
    }
}
/**
 * Configuration of a chart of type {@link ODSChartsTypes.CHOROPLETH_MAP}
 * A choropleth map is a thematic map where areas are shaded or patterned in proportion to a statistical variable.
 */
class ODSChartsChoroplethMap extends ODSChartsConfiguration {
    constructor(visualMapColorRangeMode = ODSChartsVisualMapColorRangeMode.FORCE_COLOR_SET, type = ODSChartsTypes.CHOROPLETH_MAP) {
        super(type);
        this.visualMapColorRangeMode = visualMapColorRangeMode;
    }
    getDefaultConfiguration() {
        return {
            visualMap: {
                type: 'piecewise',
                orient: 'horizontal',
                splitNumber: 6,
                itemSymbol: 'rect',
                show: true,
                left: 'center',
                outOfRange: {
                    color: ['var(--bs-border-color-subtle, #cccccc)'], // Grey color by default for delected zones
                },
                backgroundColor: 'var(--bs-body-bg)', // TODO: Replace once OUDS is developed by `--bs-color-bg-default`
                itemWidth: 48,
                itemHeight: 16,
                itemGap: 2,
                textGap: 0,
                showLabel: true,
                calculable: true,
                textStyle: {
                    align: 'left',
                    fontWeight: 400,
                    fontSize: 14,
                    fontFamily: 'var(--bs-font-sans-serif, "Helvetica Neue")',
                    rich: {
                        text: {
                            width: 48,
                            padding: [0, 0, 0, -48],
                            lineHeight: 53,
                            verticalAlign: 'bottom',
                            color: 'var(--bs-body-color, #000000)', // TODO: Replace once OUDS is developed by `--bs-color-content-default`
                        },
                    },
                },
                formatter: (value, _value2) => {
                    return '{text|' + value + '}';
                },
            },
            tooltip: {
                trigger: 'item',
                showDelay: 0,
                transitionDuration: 0.2,
            },
            color: 'transparent',
            legend: { show: false },
        };
    }
    getSerieConfiguration(serie, _themeOptions, _dataOptions, forBubbleMap) {
        if (serie.type !== 'map') {
            return {};
        }
        return Object.assign(Object.assign({}, (forBubbleMap
            ? {}
            : {
                top: '0',
                bottom: '60px',
            })), { roam: true, scaleLimit: {
                min: 0.5,
                max: 3,
            }, itemStyle: {
                areaColor: 'var(--ouds-charts-color-gridlines)',
                borderColor: 'var(--ouds-charts-color-border)',
                borderWidth: 1,
            }, emphasis: {
                label: {
                    show: false,
                    color: 'var(--bs-body-color)', // TODO: Replace once OUDS is developed by `--bs-color-content-default`
                },
                itemStyle: {
                    areaColor: 'var(--ouds-charts-color-highlight)',
                    borderColor: 'var(--ouds-charts-color-border)',
                },
            }, select: {
                disabled: true,
            } });
    }
}
/**
 * Configuration of a chart of type {@link ODSChartsTypes.BUBBLE_MAP}
 * A bubble map displays data as circles (bubbles) on a geographic map.
 */
class ODSChartsBubbleMap extends ODSChartsChoroplethMap {
    constructor(visualMapColorRangeMode) {
        super(visualMapColorRangeMode, ODSChartsTypes.BUBBLE_MAP);
    }
    getDefaultConfiguration() {
        return {
            visualMap: {
                type: 'piecewise',
                orient: 'vertical',
                splitNumber: 6,
                itemSymbol: 'circle',
                show: true,
                top: 0,
                right: 0,
                backgroundColor: 'var(--bs-body-bg)', // TODO: Replace once OUDS is developed by `--bs-color-bg-default`
                itemGap: 8,
                textGap: 8,
                showLabel: true,
                calculable: true,
                textStyle: {
                    align: 'left',
                },
                align: 'left',
                formatter: (value, value2) => {
                    return value + '-' + value2;
                },
            },
            tooltip: {
            // trigger: 'item',
            // showDelay: 0,
            // transitionDuration: 0.2,
            },
            legend: {
                show: false,
            },
            color: 'transparent',
            geo: {
                roam: true,
                scaleLimit: {
                    min: 0.5,
                    max: 3,
                },
                itemStyle: {
                    areaColor: 'var(--ouds-charts-color-gridlines)',
                    borderColor: 'var(--ouds-charts-color-border)',
                    borderWidth: 1,
                },
                emphasis: {
                    disabled: true,
                },
            },
        };
    }
    getSerieConfiguration(serie, _themeOptions, _dataOptions) {
        if (serie.type === 'map') {
            return super.getSerieConfiguration(serie, _themeOptions, _dataOptions, true);
        }
        if (serie.type === 'scatter' || serie.type === 'effectScatter') {
            return {
                coordinateSystem: 'geo',
                roam: true,
                itemStyle: {
                    areaColor: 'var(--ouds-charts-color-gridlines)',
                    borderColor: 'var(--ouds-charts-color-border)',
                    borderWidth: 2,
                    opacity: 1,
                },
                emphasis: {
                    scale: false,
                    label: {
                        // show: false,
                        color: 'var(--bs-body-color)', // TODO: Replace once OUDS is developed by `--bs-color-content-default`
                    },
                    itemStyle: {
                        color: 'var(--ouds-charts-color-highlight)',
                        borderColor: 'var(--ouds-charts-color-border)',
                        opacity: 1,
                    },
                },
            };
        }
        return {};
    }
}
/**
 * Configuration of a chart of type {@link ODSChartsTypes.HEATMAP}
 * A heatmap displays values through colors at the intersection of two category axes.
 */
class ODSChartsHeatmap extends ODSChartsConfiguration {
    constructor(visualMapColorRangeMode = ODSChartsVisualMapColorRangeMode.FORCE_COLOR_SET) {
        super(ODSChartsTypes.HEATMAP);
        this.visualMapColorRangeMode = visualMapColorRangeMode;
    }
    getDefaultConfiguration() {
        return {
            grid: {
                top: '5px',
                left: '50px',
                right: '20px',
                bottom: '100px',
            },
            visualMap: {
                textStyle: {
                    fontWeight: '400',
                    fontSize: 14,
                    color: 'var(--bs-body-color)',
                    padding: [0, 0, 0, 5],
                },
                icon: 'rect',
                itemWidth: 12,
                itemHeight: 12,
                orient: 'horizontal',
                bottom: 20,
                left: 'center',
                padding: [0, 40, 10, 40],
                type: 'piecewise',
                itemSymbol: 'rect',
            },
        };
    }
}
